import { useState, useEffect, useRef, memo } from 'react';
import { ToastProps } from 'types';
import { Draft } from 'utils/images';

interface ToastComponentProps {
  toastList: ToastProps[];
  autoDelete: boolean;
  autoDeleteTime: number;
}

/**
 * How often expiry is checked. Deadlines are absolute, so this only bounds how late a toast can
 * linger past its own time, not how long it lives.
 */
const SWEEP_INTERVAL_MS = 250;

/**
 * How much of a file name the Progress toast prints.
 *
 * The toast is sized by its content, so an unshortened name stretches the
 * whole thing across the page (UAT, IMP-1791). Cut here rather than in CSS so
 * the toast keeps the width the design gives it: a width-based clip either
 * caps the toast, which wraps its copy and reads as squashed, or gives the
 * name a box whose cut moves with the sentence around it.
 *
 * The whole name stays on the element's title attribute, so nothing is lost.
 */
export const TOAST_FILE_NAME_LIMIT = 60;

/** The ellipsis is one character, so the result never exceeds the limit by more than that. */
export const shortenToastFileName = (fileName: string): string =>
  fileName.length > TOAST_FILE_NAME_LIMIT ? `${fileName.slice(0, TOAST_FILE_NAME_LIMIT)}…` : fileName;

const Toast = (props: ToastComponentProps): JSX.Element => {
  const { toastList, autoDelete, autoDeleteTime } = props;
  const [list, setList] = useState(toastList);
  // Fallback clock start for a toast raised without a shownAt, keyed by id. A ref so it survives
  // the re-render each removal causes.
  const firstSeenRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    setList([...toastList]);
  }, [toastList]);

  const deleteToast = (id: number) => {
    const listItemIndex = list.findIndex((e: ToastProps) => e.id === id);
    const toastListItem = toastList.findIndex((e: ToastProps) => e.id === id);
    // A -1 from findIndex would make splice remove the LAST entry instead, silently dropping an
    // unrelated toast that still had time left.
    if (listItemIndex >= 0) {
      list.splice(listItemIndex, 1);
    }
    if (toastListItem >= 0) {
      toastList.splice(toastListItem, 1);
    }
    firstSeenRef.current.delete(id);
    setList([...list]);
  };

  // Each toast expires on its own absolute deadline rather than on one shared interval. The
  // previous single timer deleted the oldest toast every autoDeleteTime and was restarted
  // whenever the list changed, so a toast's lifetime depended on when the next one happened to
  // arrive, and every toast was stuck with the same duration.
  useEffect(() => {
    if (!autoDelete) {
      return;
    }

    const sweep = setInterval(() => {
      const now = Date.now();

      const expired = list.filter((toast: ToastProps) => {
        let shownAt = toast.shownAt ?? firstSeenRef.current.get(toast.id);

        if (shownAt === undefined) {
          firstSeenRef.current.set(toast.id, now);
          shownAt = now;
        }

        return now - shownAt >= (toast.autoDeleteTime ?? autoDeleteTime);
      });

      expired.forEach((toast: ToastProps) => deleteToast(toast.id));
    }, SWEEP_INTERVAL_MS);

    return () => {
      clearInterval(sweep);
    };
  }, [toastList, autoDelete, autoDeleteTime, list]);

  return (
    <>
      <div className="toster_waper">
        {list.map((toast: ToastProps, i: number) => (
          <div className={`msgbox toster ${toast.title}`}>
            <div className="msgwrapper flex flex-center flex-between">
              <div className="msg-text flex flex-center flex-between">
                {/* green right svg ⬇ */}
                {toast.title === 'Success' && (
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM14.592 7.46049C14.8463 7.13353 14.7874 6.66232 14.4605 6.40802C14.1335 6.15372 13.6623 6.21262 13.408 6.53958L9.40099 11.6914C9.31189 11.806 9.14429 11.8209 9.03641 11.7238L6.50173 9.44256C6.19385 9.16547 5.71963 9.19043 5.44254 9.49831C5.16544 9.80619 5.1904 10.2804 5.49828 10.5575L8.03296 12.8387C8.78809 13.5183 9.9613 13.4143 10.585 12.6123L14.592 7.46049Z"
                      fill="#298700"
                    />
                  </svg>
                )}
                {/* red cross svg ⬇ */}
                {toast.title === 'Danger' && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <g id="vuesax/bold/close-circle">
                      <g id="close-circle">
                        <path
                          id="Vector"
                          d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM15.36 14.3C15.65 14.59 15.65 15.07 15.36 15.36C15.21 15.51 15.02 15.58 14.83 15.58C14.64 15.58 14.45 15.51 14.3 15.36L12 13.06L9.7 15.36C9.55 15.51 9.36 15.58 9.17 15.58C8.98 15.58 8.79 15.51 8.64 15.36C8.35 15.07 8.35 14.59 8.64 14.3L10.94 12L8.64 9.7C8.35 9.41 8.35 8.93 8.64 8.64C8.93 8.35 9.41 8.35 9.7 8.64L12 10.94L14.3 8.64C14.59 8.35 15.07 8.35 15.36 8.64C15.65 8.93 15.65 9.41 15.36 9.7L13.06 12L15.36 14.3Z"
                          fill="#FF0000"
                        />
                      </g>
                    </g>
                  </svg>
                )}
                {toast.title === 'Draft' && <img src={Draft} />}

                {/* IMP-1791 asks for an animated spinner here. It is
                    indeterminate on purpose: the request returns as soon as the
                    job is scheduled, so there is no progress figure to show and
                    nothing on this page learns one -- the outcome arrives later
                    as a notification. `currentColor` takes the #0a77ff the
                    stylesheet sets, so the colour lives with the other
                    variant colours rather than in the markup. */}
                {toast.title === 'Progress' && (
                  <svg
                    className="toast_spinner"
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                    aria-hidden="true"
                    focusable="false">
                    {/* Eight spokes, as the Figma draws it. They fade in turn
                        rather than the group rotating: the glyph maps onto
                        itself every 45 degrees, so a stepped rotation would
                        look frozen and a continuous one barely moves. */}
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, index) => (
                      <line
                        key={angle}
                        className="toast_spinner_spoke"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        x1="8"
                        y1="1"
                        x2="8"
                        y2="5.2"
                        transform={`rotate(${angle} 8 8)`}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    ))}
                  </svg>
                )}

                {toast.title === 'Progress' ? (
                  // The sentence is composed here rather than passed in: the
                  // file name is styled differently from the copy around it,
                  // and a single description string cannot carry that. For
                  // this variant `description` is the file name on its own.
                  //
                  // A span with a class rather than <strong>: the Figma
                  // underlines the name, and the ticket's description, which
                  // bolds it, is the one that disagrees. The treatment itself
                  // lives in the stylesheet with the rest of this variant.
                  <p>
                    Your{' '}
                    <span className="toast_filename" title={toast.description}>
                      {shortenToastFileName(toast.description)}
                    </span>{' '}
                    upload is in progress.
                    <br />
                    You&apos;ll receive a notification when complete.
                  </p>
                ) : (
                  <p>{toast.description}</p>
                )}
              </div>
              {/* toast.id, not toastList[0].id. Dismissing the second toast
                  used to remove the first one instead, leaving the clicked one
                  on screen -- visible only when two are up at once. */}
              <div className="undo_close" onClick={() => deleteToast(toast.id)}>
                <button className="close-btn">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.2431 1.75738L1.75781 10.2427M10.2431 10.2426L1.75781 1.75732"
                      stroke="#1E2D66"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default memo(Toast);
